import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";

// ───────────────────── Helper Functions & Renderers ─────────────────────
function getSelectValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
}
function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }
  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }
  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }
  return "Could not load analysis at this time.";
}

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg
      className="animate-spin h-5 w-5 text-[#59AD31]"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      ></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content || typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {content.split("\n").map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;

        if (trimmed.startsWith("### ")) {
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        }

        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;

  if (key === "targetShare") {
    return (
      <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
        <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
      </svg>
    );
  }

  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }

  if (chartType === "area") {
    return (
      <span
        className="h-3 w-4 rounded-[1px]"
        style={{ backgroundColor: entry.color }}
      />
    );
  }

  return (
    <span
      className="h-3 w-3 rounded-[2px]"
      style={{ backgroundColor: entry.color }}
    />
  );
}

function CustomTooltip({ active, payload, label, unitType = "percent", chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>
          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? unitType === "percent"
                ? item.value.toLocaleString("en-US", {
                    style: "percent",
                    maximumFractionDigits: 1,
                  })
                : item.value.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })
              : item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function BiodiversityLegend({ payload, hiddenKeys, onToggle, chartType = "line", countryOrder = [] }) {
  if (!payload) return null;

  // Sort payload: first countries, then Global, then Target
  const specialOrder = ["Global", "targetShare"];

  const sortedPayload = [...payload].sort((a, b) => {
    const aValue = a.value || a.dataKey || "";
    const bValue = b.value || b.dataKey || "";

    // Buscar en el orden de países
    const aCountryIndex = countryOrder.indexOf(String(aValue));
    const bCountryIndex = countryOrder.indexOf(String(bValue));

    if (aCountryIndex !== -1 && bCountryIndex !== -1) {
      return aCountryIndex - bCountryIndex;
    }
    if (aCountryIndex !== -1) return -1;
    if (bCountryIndex !== -1) return 1;

    // Buscar en el orden especial
    const aSpecialIndex = specialOrder.indexOf(String(aValue));
    const bSpecialIndex = specialOrder.indexOf(String(bValue));

    if (aSpecialIndex !== -1 && bSpecialIndex !== -1) {
      return aSpecialIndex - bSpecialIndex;
    }
    if (aSpecialIndex !== -1) return -1;
    if (bSpecialIndex !== -1) return 1;

    return 0;
  });

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {sortedPayload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {entry.dataKey === "targetShare" ? (
              <svg width="20" height="3" viewBox="0 0 20 3" className="inline">
                <line x1="0" y1="1.5" x2="20" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="4,2" />
              </svg>
            ) : chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color || "#9999CC" }}
              />
            )}
            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function StackedCountryLegend({ payload, hiddenKeys, onToggle, chartType = "area" }) {
  if (!payload) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const isTarget = entry.dataKey === "targetShare";

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            {isTarget ? (
              <svg width="20" height="3" viewBox="0 0 20 3" className="inline">
                <line x1="0" y1="1.5" x2="20" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="4,2" />
              </svg>
            ) : chartType === "area" ? (
              <span
                className="h-3 w-4 rounded-[1px]"
                style={{ backgroundColor: entry.color }}
              />
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function resolvePathwayNode(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (Array.isArray(inputData.charts)) return inputData;

  if (pathway && inputData[pathway] && typeof inputData[pathway] === "object") {
    return inputData[pathway];
  }

  const keys = Object.keys(inputData);
  if (keys.length === 1 && typeof inputData[keys[0]] === "object") {
    const candidate = inputData[keys[0]];
    if (Array.isArray(candidate?.charts)) return candidate;
  }

  return null;
}

function resolveBiodiversityBlock(inputData, pathway) {
  if (!inputData || typeof inputData !== "object") return null;

  if (
    Array.isArray(inputData?.countries) ||
    inputData?.["Land where natural processes predominate"] ||
    inputData?.["Evolution of protected areas"] ||
    inputData?.["Evolution of Protected Areas by ecosystem and region"]
  ) {
    return inputData;
  }

  if (inputData?.Biodiversity) {
    return inputData.Biodiversity;
  }

  const pathwayNode = resolvePathwayNode(inputData, pathway);
  if (pathwayNode?.charts?.[0]?.Biodiversity) {
    return pathwayNode.charts[0].Biodiversity;
  }

  return null;
}

function buildBiodiversityMultiCountrySeries(shareBlock, includeGlobal = false, globalTargetShare = null) {
  if (!shareBlock || typeof shareBlock !== "object" || Array.isArray(shareBlock)) {
    return { chartData: [], countryKeys: [] };
  }

  const countryKeys = Object.keys(shareBlock).filter((k) => {
    if (k === "Global") return includeGlobal;
    return k !== "Global";
  });

  const computedGlobalTargetShare = globalTargetShare !== null
    ? globalTargetShare
    : (() => {
        if (!includeGlobal || !shareBlock["Global"]) return null;
        const global2020Row = (shareBlock["Global"] || []).find(
          (row) => Number(row.year) === 2020,
        );
        const value = Number(global2020Row?.computedShare);
        return Number.isFinite(value) ? value * 1.15 : null;
      })();

  const yearsSet = new Set();

  countryKeys.forEach((country) => {
    (shareBlock[country] || []).forEach((row) => {
      const year = Number(row.year);
      if (Number.isFinite(year)) yearsSet.add(year);
    });
  });

  const years = Array.from(yearsSet).sort((a, b) => a - b);

  const chartData = years.map((year) => {
    const row = { year };

    // Use Global 2020 as target reference when available
    if (computedGlobalTargetShare !== null) {
      row.targetShare = computedGlobalTargetShare;
    } else {
      const refCountry = includeGlobal && shareBlock["Global"] ? "Global" : countryKeys[0];
      const refRow = refCountry
        ? (shareBlock[refCountry] || []).find((r) => Number(r.year) === year)
        : null;

      row.targetShare = refRow ? Number(refRow.targetShare) || 0 : 0;
    }

    countryKeys.forEach((country) => {
      const found = (shareBlock[country] || []).find(
        (r) => Number(r.year) === year,
      );
      row[country] = found ? Number(found.computedShare) || 0 : 0;
    });

    return row;
  });

  return { chartData, countryKeys };
}

function getDynamicColor(index) {
  const palette = [
    "#183B8C",
    "#A7A9AC",
    "#10B24A",
    "#72BCE3",
    "#F6B800",
    "#C65A11",
    "#FF5579",
    "#9999CC",
    "#009B8A",
    "#FB752E",
    "#6C5CE7",
    "#00B894",
    "#E17055",
    "#0984E3",
    "#D63031",
    "#636E72",
    "#00CEC9",
    "#E84393",
    "#2D3436",
    "#FDCB6E",
  ];

  return palette[index % palette.length];
}

function getCountryOrderByLastValue(chartData, countryKeys) {
  if (!Array.isArray(chartData) || chartData.length === 0) return countryKeys;

  const lastRow = chartData[chartData.length - 1] || {};

  return [...countryKeys].sort((a, b) => {
    const av = typeof lastRow[a] === "number" ? lastRow[a] : 0;
    const bv = typeof lastRow[b] === "number" ? lastRow[b] : 0;
    return bv - av;
  });
}

const protectedRegionMetricOptions = [
  "Total Protected Area",
  "Protected Forests",
  "Protected Otherlands",
  "Protected Miscellaneous",
];

export default function BiodiversityTab({ data, pathway, pathwayId, scenathonId }) {
  const biodiversityBlock = useMemo(
    () => resolveBiodiversityBlock(data, pathway),
    [data, pathway],
  );

  const resolvedPathwayNode = useMemo(
    () => resolvePathwayNode(data, pathway),
    [data, pathway],
  );

  const effectivePathwayId = pathwayId || resolvedPathwayNode?.id || null;

  const countries = useMemo(
    () =>
      Array.isArray(biodiversityBlock?.countries)
        ? biodiversityBlock.countries
        : [],
    [biodiversityBlock],
  );

  const defaultCountry = useMemo(() => {
    if (countries.includes("Global")) return "Global";
    return countries[0] || "";
  }, [countries]);

  const [selectedProtectedCountry, setSelectedProtectedCountry] =
    useState(defaultCountry);

  const [selectedProtectedMetric, setSelectedProtectedMetric] = useState(
    "Total Protected Area",
  );

  const [hiddenKeysShare, setHiddenKeysShare] = useState([]);
  const [hiddenKeysStackedShare, setHiddenKeysStackedShare] = useState([]);
  const [hiddenKeysProtected, setHiddenKeysProtected] = useState([]);
  const [hiddenKeysProtectedRegion, setHiddenKeysProtectedRegion] = useState([]);

  const shareBlock = biodiversityBlock
    ? biodiversityBlock["Land where natural processes predominate"]
    : null;

  const shareBlockNormalized = biodiversityBlock
    ? biodiversityBlock["Land where natural processes predominate by country"]
    : null;

  const protectedBlock = biodiversityBlock
    ? biodiversityBlock["Evolution of protected areas"]
    : null;

  const protectedRegionBlock = biodiversityBlock
    ? biodiversityBlock["Evolution of Protected Areas by ecosystem and region"]
    : null;

  const biodiversityMultiCountry = useMemo(() => {
    return buildBiodiversityMultiCountrySeries(shareBlock, true);
  }, [shareBlock]);

  const biodiversityShareData = biodiversityMultiCountry.chartData;
  const biodiversityCountryKeys = biodiversityMultiCountry.countryKeys;

  const globalTargetShare = useMemo(() => {
    if (!shareBlock || !shareBlock["Global"]) return null;
    const global2020Row = (shareBlock["Global"] || []).find(
      (row) => Number(row.year) === 2020,
    );
    const value = Number(global2020Row?.computedShare);
    return Number.isFinite(value) ? value * 1.15 : null;
  }, [shareBlock]);

  const stackedCountryOrder = useMemo(() => {
    return getCountryOrderByLastValue(
      biodiversityShareData,
      biodiversityCountryKeys,
    );
  }, [biodiversityShareData, biodiversityCountryKeys]);

  const biodiversityMultiCountryNormalized = useMemo(() => {
    return buildBiodiversityMultiCountrySeries(
      shareBlockNormalized,
      false,
      globalTargetShare,
    );
  }, [shareBlockNormalized, globalTargetShare]);

  const biodiversityShareDataNormalized = biodiversityMultiCountryNormalized.chartData;
  const biodiversityCountryKeysNormalized = biodiversityMultiCountryNormalized.countryKeys;

  const stackedCountryOrderNormalized = useMemo(() => {
    return getCountryOrderByLastValue(
      biodiversityShareDataNormalized,
      biodiversityCountryKeysNormalized,
    );
  }, [biodiversityShareDataNormalized, biodiversityCountryKeysNormalized]);

  const protectedEvolutionData = useMemo(() => {
    if (Array.isArray(protectedBlock)) return protectedBlock;
    if (!protectedBlock || typeof protectedBlock !== "object") return [];

    const key = selectedProtectedCountry || defaultCountry;
    return Array.isArray(protectedBlock[key]) ? protectedBlock[key] : [];
  }, [protectedBlock, selectedProtectedCountry, defaultCountry]);

  const protectedRegionLabels = useMemo(() => {
    return Array.isArray(protectedRegionBlock?.labels)
      ? protectedRegionBlock.labels
      : [];
  }, [protectedRegionBlock]);

  const protectedRegionChartData = useMemo(() => {
    const metricBlock = protectedRegionBlock?.seriesByMetric;
    if (!metricBlock || typeof metricBlock !== "object") return [];

    const series = metricBlock[selectedProtectedMetric];
    return Array.isArray(series) ? series : [];
  }, [protectedRegionBlock, selectedProtectedMetric]);

  const countryOptions = useMemo(
    () =>
      countries.map((countryName) => ({
        value: countryName,
        label: countryName,
      })),
    [countries],
  );

  const protectedMetricOptions = useMemo(
    () =>
      protectedRegionMetricOptions.map((item) => ({
        value: item,
        label: item,
      })),
    [],
  );

  const [biodivAnalysis, setBiodivAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  const [biodivNormalizedAnalysis, setBiodivNormalizedAnalysis] = useState("");
  const [loadingBiodivNormalizedAnalysis, setLoadingBiodivNormalizedAnalysis] = useState(false);

  const [protectedAreaAnalysis, setProtectedAreaAnalysis] = useState("");
  const [loadingAnalysisProtectedArea, setLoadingAnalysisProtectedArea] =
    useState(false);

  const [protectedRegionAnalysis, setProtectedRegionAnalysis] = useState("");
  const [loadingProtectedRegionAnalysis, setLoadingProtectedRegionAnalysis] = useState(false);

  useEffect(() => {
    if (!selectedProtectedCountry && defaultCountry) {
      setSelectedProtectedCountry(defaultCountry);
    } else if (
      selectedProtectedCountry &&
      countries.length > 0 &&
      !countries.includes(selectedProtectedCountry)
    ) {
      setSelectedProtectedCountry(defaultCountry);
    }
  }, [selectedProtectedCountry, defaultCountry, countries]);

  useEffect(() => {
    setHiddenKeysProtectedRegion([]);
  }, [selectedProtectedMetric]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const payload = {
          analysisKey: "biodiversity_analysis",
          scope: "pathway",
          scenathonId,
          pathway,
          biodiversityData: {
            data: JSON.stringify(biodiversityShareData),
            title: "Land where natural processes predominate",
          },
          context: "biodiversity",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setBiodivAnalysis(
          extractAnalysisText(resData, ["biodiversityAnalysis", "analysis"])
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching analysis:", error);
          setBiodivAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAnalysis(false);
        }
      }
    };

    if (
      scenathonId &&
      pathway &&
      Array.isArray(biodiversityShareData) &&
      biodiversityShareData.length > 0
    ) {
      fetchAnalysis();
    } else {
      setBiodivAnalysis("");
      setLoadingAnalysis(false);
    }

    return () => controller.abort();
  }, [biodiversityShareData, pathway, scenathonId]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchNormalizedAnalysis = async () => {
      setLoadingBiodivNormalizedAnalysis(true);
      try {
        const payload = {
          analysisKey: "biodiversity_analysis_by_country",
          scope: "pathway",
          scenathonId,
          pathway,
          biodiversityNormalizedData: {
            data: JSON.stringify(biodiversityShareDataNormalized),
            title: "Land where natural processes predominate by country",
          },
          context: "biodiversity",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setBiodivNormalizedAnalysis(
          extractAnalysisText(resData, ["biodiversityAnalysisByCountry", "analysis"])
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching analysis:", error);
          setBiodivNormalizedAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingBiodivNormalizedAnalysis(false);
        }
      }
    };

    if (
      scenathonId &&
      pathway &&
      Array.isArray(biodiversityShareDataNormalized) &&
      biodiversityShareDataNormalized.length > 0
    ) {
      fetchNormalizedAnalysis();
    } else {
      setBiodivNormalizedAnalysis("");
      setLoadingBiodivNormalizedAnalysis(false);
    }

    return () => controller.abort();
  }, [biodiversityShareDataNormalized, pathway, scenathonId]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProtectedAnalysis = async () => {
      setLoadingAnalysisProtectedArea(true);
      try {
        const payload = {
          analysisKey: "biodiversity_analysis_protected_area",
          scope: "country",
          scenathonId,
          pathway,
          country: selectedProtectedCountry,
          protectedAreaData: {
            data: JSON.stringify(protectedEvolutionData),
            title: `Evolution of Protected Areas - ${selectedProtectedCountry}`,
          },
          context: "protected_area",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setProtectedAreaAnalysis(
          extractAnalysisText(resData, ["biodiversityAnalysisProtectedArea", "analysis"])
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching protected area analysis:", error);
          setProtectedAreaAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAnalysisProtectedArea(false);
        }
      }
    };

    if (
      scenathonId &&
      pathway &&
      selectedProtectedCountry &&
      Array.isArray(protectedEvolutionData) &&
      protectedEvolutionData.length > 0
    ) {
      fetchProtectedAnalysis();
    } else {
      setProtectedAreaAnalysis("");
      setLoadingAnalysisProtectedArea(false);
    }

    return () => controller.abort();
  }, [protectedEvolutionData, selectedProtectedCountry, pathway, scenathonId]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProtectedRegionAnalysis = async () => {
      setLoadingProtectedRegionAnalysis(true);
      try {
        const payload = {
          analysisKey: "biodiversity_protected_region_analysis",
          scope: "metric",
          scenathonId,
          pathway,
          metric: selectedProtectedMetric,
          protectedRegionData: {
            data: JSON.stringify(protectedRegionChartData),
            title: `Evolution of Protected Areas by ecosystem and region - ${selectedProtectedMetric}`,
          },
          context: "protected_area",
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt_scenathon`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Analysis API error: ${response.status}`);
        }

        const resData = await response.json();
        setProtectedRegionAnalysis(
          extractAnalysisText(resData, ["biodiversityProtectedRegionAnalysis", "analysis"])
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching protected region analysis:", error);
          setProtectedRegionAnalysis("Could not load analysis at this time.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingProtectedRegionAnalysis(false);
        }
      }
    };

    if (
      scenathonId &&
      pathway &&
      selectedProtectedMetric &&
      Array.isArray(protectedRegionChartData) &&
      protectedRegionChartData.length > 0
    ) {
      fetchProtectedRegionAnalysis();
    } else {
      setProtectedRegionAnalysis("");
      setLoadingProtectedRegionAnalysis(false);
    }

    return () => controller.abort();
  }, [protectedRegionChartData, selectedProtectedMetric, pathway, scenathonId]);

  const handleLegendClickShare = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenKeysShare((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickStackedShare = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenKeysStackedShare((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickProtected = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenKeysProtected((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickProtectedRegion = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;

    setHiddenKeysProtectedRegion((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[20px] font-medium text-[#323334]">
          Land where natural processes predominate
        </h2>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={Array.isArray(biodiversityShareData) ? biodiversityShareData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    style: "percent",
                    maximumFractionDigits: 0,
                  })
                }
              >
                <Label
                  value="Percentage of area"
                  angle={-90}
                  position="left"
                  dx={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} unitType="percent" chartType="line" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0 }}
                content={(props) => (
                  <BiodiversityLegend
                    {...props}
                    hiddenKeys={hiddenKeysShare}
                    onToggle={handleLegendClickShare}
                    chartType="line"
                    countryOrder={biodiversityCountryKeys.filter((k) => k !== "Global")}
                  />
                )}
              />

              {biodiversityCountryKeys
                .filter((country) => country !== "Global")
                .sort((a, b) => {
                  // Orden específico de países
                  const order = biodiversityCountryKeys.filter((k) => k !== "Global");
                  const indexA = order.indexOf(a);
                  const indexB = order.indexOf(b);
                  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                  if (indexA !== -1) return -1;
                  if (indexB !== -1) return 1;
                  return a.localeCompare(b);
                })
                .map((country, index) => (
                  <Line
                    key={country}
                    type="monotone"
                    dataKey={country}
                    name={country}
                    stroke={getDynamicColor(index)}
                    strokeWidth={2}
                    dot={false}
                    hide={hiddenKeysShare.includes(country)}
                  />
                ))}

              {biodiversityCountryKeys.includes("Global") && (
                <Line
                  type="monotone"
                  dataKey="Global"
                  name="Global"
                  stroke="#000000"
                  strokeWidth={3.5}
                  dot={false}
                  hide={hiddenKeysShare.includes("Global")}
                />
              )}

              <Line
                type="monotone"
                dataKey="targetShare"
                name="Target"
                stroke="#FF0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenKeysShare.includes("targetShare")}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={biodivAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[20px] font-medium text-[#323334]">
          Land where natural processes predominate by country
        </h2>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={Array.isArray(biodiversityShareDataNormalized) ? biodiversityShareDataNormalized : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                domain={[0, 1]}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    style: "percent",
                    maximumFractionDigits: 0,
                  })
                }
              >
                <Label
                  value="Percentage of area"
                  angle={-90}
                  position="left"
                  dx={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} unitType="percent" chartType="area" />}
                cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0 }}
                content={(props) => (
                  <StackedCountryLegend
                    {...props}
                    hiddenKeys={hiddenKeysStackedShare}
                    onToggle={handleLegendClickStackedShare}
                    chartType="area"
                  />
                )}
              />

              {stackedCountryOrderNormalized.map((country, index) => (
                <Area
                  key={country}
                  type="monotone"
                  dataKey={country}
                  name={country}
                  stackId="biodiversity"
                  stroke={country === "Global" ? "#000000" : getDynamicColor(index)}
                  strokeWidth={country === "Global" ? 3 : 1}
                  fill={country === "Global" ? "rgba(0,0,0,0.1)" : getDynamicColor(index)}
                  hide={hiddenKeysStackedShare.includes(country)}
                />
              ))}

              <Line
                type="monotone"
                dataKey="targetShare"
                name="Target"
                stroke="#FF0000"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                hide={hiddenKeysStackedShare.includes("targetShare")}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingBiodivNormalizedAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={biodivNormalizedAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[20px] font-medium text-[#323334]">
            Evolution of Protected Areas
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedProtectedCountry}
              onChange={(value) => setSelectedProtectedCountry(getSelectValue(value))}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(protectedEvolutionData) ? protectedEvolutionData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    notation: "compact",
                    compactDisplay: "short",
                  })
                }
              >
                <Label
                  value="1000 ha"
                  angle={-90}
                  position="left"
                  dx={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} unitType="number" chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0 }}
                content={(props) => (
                  <BiodiversityLegend
                    {...props}
                    hiddenKeys={hiddenKeysProtected}
                    onToggle={handleLegendClickProtected}
                    chartType="bar"
                  />
                )}
              />

              <Bar
                dataKey="forests"
                name="Forest"
                stackId="a"
                fill="#4CAF50"
                hide={hiddenKeysProtected.includes("forests")}
              />

              <Bar
                dataKey="otherLands"
                name="Otherlands"
                stackId="a"
                fill="#FFC107"
                hide={hiddenKeysProtected.includes("otherLands")}
              />

              <Bar
                dataKey="miscellaneous"
                name="Miscellaneous"
                stackId="a"
                fill="#607D8B"
                hide={hiddenKeysProtected.includes("miscellaneous")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingAnalysisProtectedArea ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={protectedAreaAnalysis} />
          )}
        </div> */}
      </section>

      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[20px] font-medium text-[#323334]">
            Evolution of Protected Areas by ecosystem and region
          </h2>

          <div className="w-full max-w-sm">
            <Select
              label="Indicators"
              value={selectedProtectedMetric}
              onChange={(value) => setSelectedProtectedMetric(getSelectValue(value))}
              options={protectedMetricOptions}
              placeholder="Select indicator"
              disabled={protectedMetricOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(protectedRegionChartData) ? protectedRegionChartData : []}
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>

              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(v) =>
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })
                }
              >
                <Label
                  value="1000 ha"
                  angle={-90}
                  position="left"
                  dx={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>

              <Tooltip
                content={(props) => <CustomTooltip {...props} unitType="number" chartType="bar" />}
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />

              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0 }}
                content={(props) => (
                  <StackedCountryLegend
                    {...props}
                    hiddenKeys={hiddenKeysProtectedRegion}
                    onToggle={handleLegendClickProtectedRegion}
                    chartType="bar"
                  />
                )}
              />

              {protectedRegionLabels.map((label, index) => (
                <Bar
                  key={label}
                  dataKey={label}
                  name={label}
                  stackId="protected-region"
                  fill={getDynamicColor(index)}
                  stroke={getDynamicColor(index)}
                  hide={hiddenKeysProtectedRegion.includes(label)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* <div className="w-full min-h-[100px]">
          {loadingProtectedRegionAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <NativeAnalysis content={protectedRegionAnalysis} />
          )}
        </div> */}
      </section>
    </div>
  );
}